/**
 * The saloon view — the same table, seen from a chair.
 *
 * ============================== WHAT THIS IS ===================================
 * Mock m11 (`mockups/m11-saloon-2026-09-05.html`, built by `source/build-mock.py`) as a
 * component: the plate tilted 33° under a 2600px perspective, the cast STANDING round
 * the far side behind their places at the rail, the player's own character standing
 * bottom-left, their two cards bottom-centre over the near rail, the action bar
 * bottom-right.
 *
 * ## One table, two projections
 *
 * This is not a second table. It takes EXACTLY `TopDownTable`'s props (plus the
 * persona's slug, for the standing figure, and the view toggle) and draws EXACTLY its
 * children — `SeatTag`, `TimerRing`, `SeatPeek`, `SeatBet`, `DealerButton`, `DeckSpot`,
 * `Board`, `PotWrap`, `HeroHand`, `ChipStacks` through those, `FlightLayer`,
 * `BubbleLayer`, the two live regions, the action bar the route builds — from
 * `parts.tsx`. What is different is WHERE: every piece is placed in screen space at
 * `project()` of its felt coordinate (`lib/saloon-geometry.ts`, the mock's own
 * function), and the only thing that is actually tilted is the plate image. Plates,
 * cards, chips, the board and the bubbles stay flat, because they have to be read.
 *
 * The `data-td` anchors are the same, on the same kinds of element, so the flight
 * layer and the bubble placer — which find everything by measuring those anchors —
 * work under this projection unchanged. The one addition is `data-td="face"`: a
 * standing character's face, which the bubble placer treats as a HARD obstacle (a
 * bubble over a face is worse than one over a card). See `flight.tsx`.
 *
 * ## What is the saloon's own
 *
 *   - the ROOM behind the table (the saloon plate m11 used, dimmed and vignetted) and
 *     the lamp;
 *   - the STANDING CAST: each opponent's keyed three-quarter turnaround crop
 *     (`art/casting.ts` `standing()`), mirrored to face the middle, scaled by depth, cut
 *     by the rail. Never the profile: side-on, the face cannot be seen (Tim, 10 Sep). A
 *     crop the keyer chewed is not stood up — the seat shows its portrait disc on the
 *     rail plate instead;
 *   - the HERO'S FIGURE, bottom-left: the persona's full-body crop, or a silhouette
 *     until one is picked.
 *
 * Sizing is ONE length: `--u`, one stage pixel of the mock's 1600 x 900 stage, fitted
 * to the box by a container query (`saloon.css`). No resize listener, no measurement:
 * the same CSS-only rule the top-down table was built on.
 * ===============================================================================
 */
import type { ReactNode } from "react";
import type { TableState } from "@engine/table-state";
import { FlightLayer, BubbleLayer } from "./flight";
import { durationsFor } from "./pace";
import {
  Portrait, TimerRing, SeatPeek, SeatTag, SeatBet, DealerButton, DeckSpot, Board, PotWrap,
  HeroHand, BoardStatus, TalkStatus, TopBar, useBubbles, useCollected, motionVars, seatClass,
  seatIsEmpty,
} from "./parts";
import type { TopDownTableProps } from "./TopDownTable";
import {
  saloonSeats, HERO, MIDDLE, STAGE, PLANE, TILT, D, RAIL_CUT, type Projected,
} from "../lib/saloon-geometry";
import { standing, seatSlug, type StandingCrop } from "../art/casting";
import "./topdown.css";
import "./saloon.css";

export interface SaloonTableProps extends TopDownTableProps {
  /**
   * The player's persona, as a roster slug — for the standing figure. `avatarUrl`
   * (which `TopDownTable` also takes) is still the disc on their plate. Null: the
   * silhouette, which is the truth until they pick one.
   */
  persona?: string | null;
  /** The room behind the table. m11's: the warm bar-and-stair plate. */
  backdrop?: string;
  /** The Saloon / Top-down switch, drawn in the table's own bar when it has one. */
  toggle?: ReactNode;
}

const NORMAL = durationsFor("normal");
/** The 2200 tier: at 1920 wide the plate is drawn about 1,340 CSS px across. */
const PLATE = "/art/table/poker-topdown-2200.webp";
const BACKDROP = "/art/scenes/saloon-02-2000.webp";

/** Stage coordinates and a depth scale, as the custom properties `saloon.css` reads. */
const at = (p: { x: number; y: number; s?: number }) => ({
  "--x": p.x, "--y": p.y, "--s": p.s ?? 1,
} as React.CSSProperties);

/** Which way a figure should look: toward the middle of the table. */
function mirrorFor(deg: number, crop: StandingCrop): boolean {
  if (deg > 90) return crop.facing === "l";
  if (deg < 90) return crop.facing === "r";
  return false;
}

/**
 * A standing figure: the crop, cut by the rail, and the face on it.
 *
 * The face is two boxes in one place: `data-td="face"`, which the bubble placer may not
 * cover, and inside it `data-td="av"`, the seat's portrait anchor, which is what a
 * bubble points at and what a pot flies to when there is no stack number to land on.
 */
function Figure({ crop, box, mirror, hero = false, z }: {
  crop: StandingCrop | null;
  box: { cx?: number; left?: number; top: number; h: number; faceCx?: number };
  mirror: boolean;
  hero?: boolean;
  z: number;
}) {
  const aspect = crop?.aspect ?? SILHOUETTE_ASPECT;
  const w = box.h * aspect;
  const face = crop?.face ?? SILHOUETTE_FACE;
  /* The face's x in the crop, after mirroring. */
  const fx = mirror ? 1 - (face.x + face.w) : face.x;
  /* Where the crop's left edge goes. A hero is placed by the FACE, so a wide crop
     (a coat, a rope, a pan) never pushes their head across the table. */
  const left = box.faceCx !== undefined ? box.faceCx - (fx + face.w / 2) * w
    : box.left !== undefined ? box.left : (box.cx ?? 0) - w / 2;
  const faceBox = {
    "--x": left + fx * w, "--y": box.top + face.y * box.h,
    "--w": face.w * w, "--h": face.h * box.h,
  } as React.CSSProperties;
  return (
    <>
      {crop ? (
        <img className={"sa-figure" + (hero ? " sa-figure-hero" : "")} src={crop.url} alt="" aria-hidden="true"
             data-td="figure" draggable={false}
             style={{ "--x": left, "--y": box.top, "--h": box.h, "--w": w, "--flip": mirror ? -1 : 1,
                      "--cut": RAIL_CUT, zIndex: z } as React.CSSProperties} />
      ) : (
        <Silhouette left={left} top={box.top} h={box.h} w={w} z={z} />
      )}
      <span className="sa-face" data-td="face" style={{ ...faceBox, zIndex: z }} aria-hidden="true">
        <span className="sa-av" data-td="av" />
      </span>
    </>
  );
}

/* The silhouette's own proportions, so it is placed by the same rule as a crop. */
/** The hero figure's stacking level — see where it is used (and `phase7-view.test.ts`). */
const HERO_FIGURE_Z = 55;

const SILHOUETTE_ASPECT = 0.36;
const SILHOUETTE_FACE = { x: 0.39, y: 0.09, w: 0.22, h: 0.1 };

/**
 * The player before they have picked a face: a figure, not a hole.
 *
 * The top-down seat's blank is a cream silhouette in a disc; this is the same idea
 * standing up — a hat, a head, shoulders and a coat in the house's ink, lit faintly
 * from the lamp, where the persona will stand once there is one.
 */
function Silhouette({ left, top, h, w, z }: { left: number; top: number; h: number; w: number; z: number }) {
  return (
    <svg className="sa-figure sa-silhouette" viewBox="0 0 36 100" preserveAspectRatio="none"
         data-td="figure" role="img" aria-label="You — no portrait chosen yet"
         style={{ "--x": left, "--y": top, "--h": h, "--w": w, "--flip": 1, "--cut": RAIL_CUT, zIndex: z } as React.CSSProperties}>
      <defs>
        <linearGradient id="sa-sil" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2e241c" />
          <stop offset=".5" stopColor="#1c1612" />
          <stop offset="1" stopColor="#0e0b09" />
        </linearGradient>
      </defs>
      {/* One shape: the brim drawn over the crown and the head, the coat under the
          chin, so the lamp's rim light (`saloon.css`) traces the outline only. */}
      <g fill="url(#sa-sil)">
        <ellipse cx="18" cy="13.6" rx="3.8" ry="4.4" />
        <path d="M18 18.6c-4.6 0-8.4 1.4-10 4.8L6 44c-.2 2 .4 3.4 1.6 3.4L8.8 33l.8 67h16.8l.8-67 1.2 14.4c1.2 0 1.8-1.4 1.6-3.4l-2-20.6c-1.6-3.4-5.4-4.8-10-4.8z" />
        <path d="M12.6 8.4L13.2 3.4C13.6 2.2 15.6 1.6 18 1.6s4.4.6 4.8 1.8l.6 5z" />
        <path d="M6 9.2C9 8 13 7.6 18 7.6s9 .4 12 1.6c-1.5 1-6 1.4-12 1.4S7.5 10.2 6 9.2z" />
      </g>
    </svg>
  );
}

export function SaloonTable({
  state, lastAction, context, wallet, chrome = "own", timers, expressions, taunts, shown, faces,
  actions, dealt, flights, motion, settings, onSettings, avatarUrl = null,
  deck = "none", payoff = null, potFlying = false, potFrozen = false,
  persona = null, backdrop = BACKDROP, toggle,
}: SaloonTableProps) {
  const heroIndex = state ? state.seats.findIndex((s) => s.isSelf) : -1;
  const collected = useCollected(state, potFrozen);
  const { bubbles, newest } = useBubbles(state, taunts, heroIndex);

  if (!state) {
    return (
      <div className="td-frame">
        <div className="td-app td-app-saloon td-app-empty"><p className="muted">Deal a hand to begin.</p></div>
      </div>
    );
  }

  const n = state.seats.length;
  const hero = heroIndex >= 0 ? state.seats[heroIndex]! : null;
  const seats = saloonSeats(n, heroIndex);
  const bySeat = new Map(seats.map((x) => [x.seat, x]));

  const btn: Projected = state.button === heroIndex
    ? HERO.button
    : bySeat.get(state.button)?.button ?? HERO.button;

  /* The hero's figure: their persona's crop, placed by the face. */
  const heroCrop = standing(persona, "three-quarter");
  /* The player faces into the room: toward screen-right, as m11's Marshal does. */
  const heroMirror = heroCrop ? heroCrop.facing === "l" : false;

  return (
    <div className="td-frame">
    <div className="td-app td-app-saloon" style={{
      ...motionVars(motion),
      "--tilt": `${TILT}deg`, "--persp": D, "--plane-w": PLANE.w, "--plane-h": PLANE.h,
      "--plane-x": PLANE.cx - PLANE.w / 2, "--plane-y": PLANE.cy - PLANE.h / 2,
      "--stage-w": STAGE.w, "--stage-h": STAGE.h,
    } as React.CSSProperties}>
      {/* The room. Behind everything, the whole box, not only the stage. */}
      <div className="sa-room" aria-hidden="true" style={{ "--backdrop": `url(${backdrop})` } as React.CSSProperties}>
        <span className="sa-lamp" />
      </div>

      <div className="sa-stage">
        {/* ------------------------------------------------------ the opponents */}
        {state.seats.map((s, i) => {
          if (s.isSelf) return null;
          if (seatIsEmpty(s)) return null;
          const g = bySeat.get(i);
          if (!g) return null;
          /*
           * Every seat stands in THREE-QUARTER, the two ends of the arc included —
           * mirrored so they face the middle. Tim, 10 Sep, on the first renders: "don't
           * use any characters that are sideways, can't even see their face." The
           * profile crops are keyed and synced, and not stood up anywhere.
           */
          const crop = standing(seatSlug(faces, i, s.identity.name), "three-quarter");
          const z = 10 + Math.round((g.figure.s - 0.9) * 200);
          return (
            <div key={`seat${i}`} data-td="seat" data-seat={i}
                 className={seatClass(s, i, state, payoff, shown) + " sa-seat" + (crop ? "" : " sa-seat-disc")}>
              {crop && <Figure crop={crop} box={{ cx: g.figure.cx, top: g.figure.top, h: g.figure.h }}
                               mirror={mirrorFor(g.deg, crop)} z={z} />}
              <SeatTag s={s} motion={motion} className={"sa-plate" + (s.allIn ? " sa-plate-allin" : "")}
                       style={at(g.plate)}>
                {/* The fallback: no standing figure, so the face is a disc on the plate —
                    the same Portrait the top-down seat draws, expression and all. */}
                {!crop && (
                  <span className="sa-disc" data-td="av">
                    <Portrait name={s.identity.name} expression={expressions?.[i]} slug={faces?.[i]}
                              className="td-av-img" />
                  </span>
                )}
                <TimerRing timer={timers?.[i]} variant="bar" />
              </SeatTag>
              <SeatPeek s={s} i={i} shown={shown} dealt={dealt} className="sa-peek" style={at(g.cards)} />
              <SeatBet s={s} i={i} left={false} flights={!!flights} className="sa-bet" style={at(g.bet)} />
            </div>
          );
        })}

        {/* ---------------------------------------------------------- the plate */}
        <div className="sa-scene" aria-hidden="true">
          <img className="sa-plate-img td-plate" src={PLATE} alt="" />
        </div>

        {/* ------------------------------------------------------------ the hero */}
        {hero && (
          <div data-td="seat" data-seat={heroIndex}
               className={seatClass(hero, heroIndex, state, payoff, shown) + " sa-seat sa-seat-hero"}>
            {/* z 55: in front of the tilted table (50), BEHIND everything placed on it
                (plates, pairs, bets, the button: 60). The mock drew the hero last, over
                the plates; with full-body crops the hat reaches the first seat's plate
                at the left end of the arc and hid the start of its timer bar. Scenery
                gives way to the numbers. */}
            <Figure crop={heroCrop} mirror={heroMirror} hero z={HERO_FIGURE_Z}
                    box={{ faceCx: HERO_FACE_CX, top: HERO.figure.top, h: HERO.figure.h }} />
            <SeatTag s={hero} motion={motion} className={"sa-plate sa-heroplate" + (hero.allIn ? " sa-plate-allin" : "")}>
              <span className="sa-disc sa-herodisc">
                <Portrait name={hero.identity.name} override={avatarUrl} className="td-av-img" />
              </span>
              <TimerRing timer={timers?.[heroIndex]} variant="bar" />
            </SeatTag>
            {/* Shown cards turn over at the player's plate, above it. */}
            <SeatPeek s={hero} i={heroIndex} shown={shown} dealt={dealt} className="sa-peek sa-heropeek"
                      style={at(HERO.shown)} />
            <SeatBet s={hero} i={heroIndex} left={false} flights={!!flights} className="sa-bet" style={at(HERO.bet)} />
          </div>
        )}

        {/* ---------------------------------------------------------- the middle */}
        <DealerButton state={state} motion={motion} className="sa-dbtn" style={at(btn)} />
        <DeckSpot deck={deck} className="sa-deck" style={at(MIDDLE.deck)} />
        <Board board={state.board} className="sa-board" style={at(MIDDLE.board)} />
        <PotWrap pot={state.pot} collected={collected} potFlying={potFlying} motion={motion}
                 className="sa-pot" style={{
                   ...at(MIDDLE.potPill),
                   "--chips-dx": MIDDLE.potChips.x - MIDDLE.potPill.x,
                   "--chips-dy": MIDDLE.potChips.y - MIDDLE.potPill.y,
                 } as React.CSSProperties} />

        {hero && (
          <div className="td-herozone sa-herozone">
            <HeroHand hero={hero} heroIndex={heroIndex} dealt={dealt} payoff={payoff} />
          </div>
        )}
      </div>

      {/* The table's own bar, over the scene, when there is no room around it. */}
      {chrome === "own" && (
        <TopBar state={state} context={context} lastAction={lastAction} wallet={wallet}
                settings={settings} onSettings={onSettings} className="sa-top" extra={toggle} />
      )}

      {/* The action bar the route builds — one bar, a third placement. */}
      <div className="td-actions sa-actions">{actions}</div>

      <BoardStatus state={state} />
      {flights && motion && <FlightLayer bus={flights} durations={motion} />}
      {bubbles.length > 0 && <BubbleLayer bubbles={bubbles} durations={motion ?? NORMAL} persist={!motion} />}
      <TalkStatus state={state} newest={newest} />
    </div>
    </div>
  );
}

/**
 * Where the hero's face sits, stage x: m11's Marshal, whose head is centred about 145px
 * in from the left edge (`.heroimg { left: -10px }`, a waist crop mirrored). A full-body
 * crop is wider than a waist crop, so it is placed by its face rather than by its box.
 */
const HERO_FACE_CX = 145;
